export default function LakeCharlesTravelTimingSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Travel &amp; Passport Timing for Lake Charles Residents
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Understanding how Lake Charles travelers actually move helps us anticipate the passport urgencies that arise here. From petrochemical plant turnaround timelines to casino industry conferences, Southwest Louisiana travel operates on a rhythm driven by industrial cycles and Gulf Coast tourism.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-building-4-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Petrochemical &amp; LNG Industry Travel</h3>
                <p className="text-gray-400 text-xs">Saudi Arabia, Netherlands, UK, South Korea</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Lake Charles hosts one of the largest concentrations of LNG export terminals and chemical manufacturing facilities in North America. Plant turnarounds, technology licensing negotiations, and equipment commissioning assignments send engineers and project managers to Saudi Arabia, the Netherlands, South Korea, and the UK — often with less than two weeks notice. The industrial cycle here is relentless, and passport preparation must match it.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Saudi Arabia and Qatar require six months passport validity. Some companies do not verify passport expiration until travel is booked — do not wait for HR to flag it.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-gamepad-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Casino &amp; Hospitality Industry</h3>
                <p className="text-gray-400 text-xs">Macau, Singapore, Las Vegas, UK gaming conferences</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Lake Charles casino resorts are among the largest employers in Southwest Louisiana. Gaming executives, technology vendors, and hospitality professionals attend industry conferences in Macau, Singapore, and London. Training programs for casino staff visiting Asian gaming hubs require current passports, and many employees have not traveled internationally since before the pandemic, leaving their passports expired or close to expiration.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Macau requires a separate visa for US citizens but your passport must have at least six months validity. Singapore requires it too. Check both before booking industry travel.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-ship-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Gulf Coast Cruises &amp; Family Travel</h3>
                <p className="text-gray-400 text-xs">Galveston, New Orleans cruise ports — Bahamas, Caribbean, Mexico</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Lake Charles residents have convenient access to two major cruise ports — Galveston (100 miles west) and New Orleans (200 miles east). Southwest Louisiana families are avid cruise travelers, booking sailings to the Bahamas, Mexico, Jamaica, and Belize. Last-minute cruise deals and holiday booking windows often come together quickly, and discovering a child's passport is expired the week before sailing is one of the most common urgent passport situations we handle.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Mexico cruise ports (Cozumel, Progreso) require a valid passport for all passengers. Do not assume a birth certificate is enough — verify cruise line requirements before booking.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-graduation-cap-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">McNeese State Study Abroad</h3>
                <p className="text-gray-400 text-xs">Europe, Latin America, engineering exchange programs</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              McNeese State University's engineering and business programs send students abroad for co-op placements, semester exchanges, and faculty-led international courses. Engineering students often have international placement opportunities confirmed with limited lead time. Many are first-time passport applicants who need guidance through the acceptance clerk process and expedited processing after their in-person visit.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> First-time McNeese applicants need an in-person acceptance clerk visit before we can expedite. Apply the week you receive your program confirmation — not the month before departure.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-flight-takeoff-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">Lake Charles Regional Airport (LCH) &amp; Connections</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              Lake Charles Regional Airport (LCH) connects Southwest Louisiana travelers to Dallas-Fort Worth and other major hubs. Many Lake Charles residents also drive to Houston Bush Intercontinental (IAH, ~100 miles) or Louis Armstrong New Orleans International (MSY, ~200 miles) for direct international flights. Your passport must be valid and in hand before any international connection — whether departing from LCH or a connecting hub.
            </p>
          </div>
          <a
            href="https://fastpassportcenter.com/checkout/"
            className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0"
          >
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}